/**
 * MetadataEntityTable - Auto-generates table from entity metadata
 *
 * Uses DRR-065 metadata discovery to render ANY entity type
 * No hardcoded columns - all driven by metadata.fields
 *
 * Built on top of Tabulator for advanced table features:
 * - Sorting, filtering, pagination
 * - Movable/resizable columns
 * - Customizable styling
 */
// @ts-nocheck


import React, { useMemo, useRef, useEffect } from 'react';
import { Container, LoadingOverlay, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import 'tabulator-tables/dist/css/tabulator.min.css';
import { TabulatorFull, ColumnDefinition, RowComponent } from 'tabulator-tables';

export interface MetadataEntityTableProps {
  /** Entity type name (e.g., 'Employee', 'Compensation') */
  entityType: string;
  /** Data rows to display */
  data: Record<string, any>[];
  /** Callback when row is clicked */
  onRowClick?: (row: Record<string, any>) => void;
  /** Subgraph name (e.g., 'payroll') */
  subgraph?: string;
  /** Fields to display (defaults to all from data keys) */
  fields?: string[];
  /** Loading state */
  loading?: boolean;
  /** Error message to display */
  error?: string;
  /** Custom column definitions to override auto-generated ones */
  customColumns?: ColumnDefinition[];
}

export function MetadataEntityTable({
  entityType,
  data,
  onRowClick,
  subgraph = 'payroll',
  fields,
  loading = false,
  error,
  customColumns,
}: MetadataEntityTableProps) {
  const tableRef = useRef<HTMLDivElement>(null);
  const tabulatorRef = useRef<any>(null);

  // Auto-discover fields from data if not provided
  const displayFields = useMemo(() => {
    if (fields) return fields;
    if (data.length > 0 && data[0]) {
      return Object.keys(data[0]).filter(
        key => !key.startsWith('_') && key !== '__typename'
      );
    }
    return [];
  }, [fields, data]);

  // Build Tabulator columns from fields
  const columns: ColumnDefinition[] = useMemo(() => {
    if (customColumns) return customColumns;

    return displayFields.map((field, idx) => ({
      title: field.charAt(0).toUpperCase() + field.slice(1),
      field: field,
      width: idx === 0 ? 200 : 150,
      hozAlign: typeof data[0]?.[field] === 'number' ? 'right' : 'left',
    }));
  }, [displayFields, data, customColumns]);

  // Initialize Tabulator
  useEffect(() => {
    if (!tableRef.current) return;

    if (tabulatorRef.current) {
      // Update existing table
      tabulatorRef.current.setData(data);
    } else {
      // Create new table
      tabulatorRef.current = new TabulatorFull(tableRef.current, {
        columns: columns,
        data: data,
        layout: 'fitColumns',
        height: '500px',
        pagination: true,
        paginationSize: 50,
        movableColumns: true,
        responsiveLayout: 'collapse',
        rowClick: (e: UIEvent, row: RowComponent) => {
          onRowClick?.(row.getData());
        },
      });
    }
  }, [columns, data, onRowClick]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tabulatorRef.current) {
        tabulatorRef.current.destroy();
      }
    };
  }, []);

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error}
      </Alert>
    );
  }

  return (
    <Container pos="relative" fluid>
      <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} />
      <div ref={tableRef} />
    </Container>
  );
}
